// frontend/src/components/AddTorrentDialog.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
  IconButton, alpha, useTheme, CircularProgress, styled, Tabs, Tab, List, ListItem,
  ListItemText, ListItemIcon, Divider, Chip, Tooltip, Alert, MenuItem, Checkbox, // <-- Added Checkbox
  ListItemButton // <-- Added ListItemButton
} from '@mui/material';
import {
  Close as CloseIcon, Add as AddIcon, Link as LinkIcon, UploadFile as UploadFileIcon,
  Search as SearchIcon, Movie as MovieIcon, Tv as TvIcon, Download as DownloadIcon,
  People as SeedsIcon, CloudDownload as PeersIcon, Storage as SizeIcon, CheckBoxOutlineBlank, CheckBox // <-- Added Checkbox icons
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../services/api';
import toast from 'react-hot-toast';

// Helper function to generate a unique key for list items
const generateTorrentKey = (torrent) => {
    // Combine provider, title, and potentially size/seeds for uniqueness
    return `${torrent.provider || 'unknown'}-${torrent.title || 'unknown'}-${torrent.size || '0'}-${torrent.seeds || 0}`;
};


const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

function formatBytes(bytes, decimals = 2) {
    if (!bytes || bytes === 0) return '0 Bytes';

    // Check if the size is already formatted (contains unit like GB, MB, etc.)
    if (typeof bytes === 'string') {
        // Check if it already has a size unit
        const sizeUnits = ['B', 'KB', 'MB', 'GB', 'TB', 'Bytes'];
        const hasUnit = sizeUnits.some(unit => bytes.includes(unit));
        if (hasUnit) {
            return bytes; // Return as-is if already formatted
        }
    }

    const numBytes = parseFloat(bytes); // Parse string size if needed
    if (isNaN(numBytes)) return 'N/A';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    if (i < 0 || i >= sizes.length) {
       return parseFloat(numBytes.toFixed(dm)) + ' Bytes';
    }
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}


function AddTorrentDialog({ open, onClose, initialSearch, initialType, initialTab }) {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0); // 0: Manual, 1: Search

  // Open on a specific tab (e.g. the Torrents page opens straight to Search)
  useEffect(() => {
    if (open && initialTab != null) setTabValue(initialTab);
  }, [open, initialTab]);

  // Manual Add State
  const [magnetLinks, setMagnetLinks] = useState('');
  const [torrentFiles, setTorrentFiles] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('Movies'); // 'Movies' or 'TV'
  const [searchSort, setSearchSort] = useState('size'); // 'size' (default) | 'seeds'
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  // *** NEW: Use a Set for efficient add/remove/check of selected torrents ***
  const [selectedTorrents, setSelectedTorrents] = useState(new Set());

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear other tab's state
    if (newValue === 0) { // Switched to Manual
        resetSearchForm();
    } else { // Switched to Search
        resetManualForm();
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setTorrentFiles(files);
      setMagnetLinks('');
    }
  };

  const handleManualSubmit = async () => {
    setManualLoading(true);
    try {
      const formData = new FormData();
      if (magnetLinks) {
        formData.append('magnetLinks', magnetLinks);
      } else if (torrentFiles.length > 0) {
        for (const file of torrentFiles) {
          formData.append('torrentFiles', file);
        }
      } else {
        toast.error('Please provide magnet links or torrent files.');
        setManualLoading(false);
        return;
      }

      const response = await api.post('/torrents/add', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const r = response.data || {};
      if (r.added === 0 && r.expected > 0) {
        toast.error(r.message || 'No torrent was added — the link may be dead or already in qBittorrent.');
        setManualLoading(false);
        return; // keep the dialog open for a correction
      }
      toast.success(r.message || 'Torrent(s) added.');
      onClose(); // Close dialog on success
      resetManualForm();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add torrents.');
    } finally {
      setManualLoading(false);
    }
  };

  const handleSearch = async (queryArg, sortArg) => {
      // queryArg may be an event (from onClick) — only treat strings as an explicit query.
      const q = (typeof queryArg === 'string' ? queryArg : searchQuery);
      const type = (typeof queryArg === 'string' && initialType) ? initialType : searchType;
      const sort = (typeof sortArg === 'string' ? sortArg : searchSort);
      if (!q.trim()) {
          setSearchError('Please enter a search term.');
          return;
      }
      setSearchLoading(true);
      setSearchError('');
      setSearchResults([]);
      setSelectedTorrents(new Set()); // Clear selection on new search
      try {
          const response = await api.post('/torrents/search', {
              query: q,
              type: type,
              sort: sort
          });
          // Assign a unique key to each result *before* setting state
          const resultsWithKeys = response.data.map(r => ({ ...r, key: generateTorrentKey(r) }));
          setSearchResults(resultsWithKeys);

          if (resultsWithKeys.length === 0) {
              setSearchError('No results found.');
          }
      } catch (error) {
          const errMsg = error.response?.data?.error || 'Search failed.';
          setSearchError(errMsg);
          toast.error(`Search failed: ${errMsg}`);
      } finally {
          setSearchLoading(false);
      }
  };

  // *** MODIFIED: Handle selecting/deselecting multiple torrents ***
  const handleSearchResultSelect = (torrent) => {
    setSelectedTorrents(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(torrent.key)) { // Use the unique key
        newSelected.delete(torrent.key); // Deselect if already selected
      } else {
        newSelected.add(torrent.key); // Select if not selected
      }
      return newSelected;
    });
  };

  // *** MODIFIED: Add all selected torrents ***
  const handleAddSelectedTorrents = async () => {
    if (selectedTorrents.size === 0) {
      toast.error('No torrents selected to add.');
      return;
    }

    // Find the full torrent objects corresponding to the selected keys
    const magnetsToAdd = searchResults
        .filter(result => selectedTorrents.has(result.key) && result.magnet) // Ensure it has a magnet
        .map(result => result.magnet);


    if (magnetsToAdd.length === 0) {
        toast.error('Selected torrent(s) are missing magnet links.');
        return;
    }

    setSearchLoading(true); // Reuse search loading state
    try {
      // Send the array of magnet links
      const response = await api.post('/torrents/add', {
        magnetLinksArray: magnetsToAdd // Use the new array field
      });
      const r = response.data || {};
      if (r.added === 0 && r.expected > 0) {
        // qBittorrent said Ok but nothing appeared (dead source link / duplicate).
        // Keep the dialog open so another result can be picked.
        toast.error(r.message || 'No torrent was added — the source link may be dead.');
      } else if (r.expected > 0 && r.added < r.expected) {
        toast(r.message || `${r.added} of ${r.expected} torrents added.`, { icon: '⚠️' });
        onClose();
        resetSearchForm();
      } else {
        toast.success(`${r.added ?? magnetsToAdd.length} torrent(s) added.`);
        onClose();
        resetSearchForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add selected torrents.');
    } finally {
      setSearchLoading(false);
    }
  };


  const resetManualForm = () => {
      setMagnetLinks('');
      setTorrentFiles([]);
      setManualLoading(false); // Make sure loading state is also reset
  };

  const resetSearchForm = () => {
      setSearchQuery('');
      setSearchType('Movies');
      setSearchSort('size');
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError('');
      setSelectedTorrents(new Set()); // Reset selection
  };

  // Reset all state when dialog is closed externally
  useEffect(() => {
    if (!open) {
      resetManualForm();
      resetSearchForm();
      setTabValue(0); // Reset to first tab
    }
  }, [open]);

  // When opened with a prefilled query (e.g. the calendar's "Search for download"),
  // jump to the Search tab and run the search automatically.
  const autoSearchRef = useRef(null);
  useEffect(() => {
    if (open && initialSearch && autoSearchRef.current !== initialSearch) {
      autoSearchRef.current = initialSearch;
      setTabValue(1);
      setSearchType(initialType || 'Movies');
      setSearchQuery(initialSearch);
      handleSearch(initialSearch);
    }
    if (!open) autoSearchRef.current = null;
  }, [open, initialSearch, initialType]);


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md" // Increase width for search results
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, y: -50 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 50 },
        sx: {
          borderRadius: 4,
          backdropFilter: 'blur(10px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          height: '80vh', // Set a max height
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div">
            Add New Torrent(s)
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="Add Manually" icon={<AddIcon />} iconPosition="start" />
          <Tab label="Search Torrents" icon={<SearchIcon />} iconPosition="start" />
        </Tabs>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ flexGrow: 1, overflowY: 'auto', p: tabValue === 1 ? 0 : 3 }}>
        {/* --- Manual Add Tab --- */}
        {tabValue === 0 && (
          <Box>
            <Box my={2}>
              <TextField
                fullWidth label="Magnet Links (one per line)" variant="outlined" multiline rows={4}
                value={magnetLinks}
                onChange={(e) => { setMagnetLinks(e.target.value); setTorrentFiles([]); }}
                placeholder="magnet:?xt=urn:btih:..."
                InputProps={{ startAdornment: <LinkIcon color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1.5 }} /> }}
              />
            </Box>
            <Typography variant="body2" textAlign="center" color="text.secondary"> OR </Typography>
            <Box my={2} p={3} border={`2px dashed ${theme.palette.divider}`} borderRadius={2} textAlign="center">
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} color="primary">
                Upload Torrent File(s)
                <VisuallyHiddenInput type="file" onChange={handleFileChange} accept=".torrent" multiple />
              </Button>
              {torrentFiles.length > 0 && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Selected: {torrentFiles.map(f => f.name).join(', ')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* --- Search Tab --- */}
        {tabValue === 1 && (
          <Box display="flex" flexDirection="column" height="100%">
            {/* Search Input Area */}
             <Box p={2} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
               <Box display="flex" gap={2} mb={2}>
                 <TextField
                   fullWidth label="Search Term" variant="outlined" size="small"
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                 />
                 <TextField select label="Type" value={searchType} onChange={(e) => setSearchType(e.target.value)} size="small" sx={{ minWidth: 120 }}>
                    <MenuItem value={'Movies'}><MovieIcon sx={{ mr: 1, fontSize: '1rem' }} /> Movies</MenuItem>
                    <MenuItem value={'TV'}><TvIcon sx={{ mr: 1, fontSize: '1rem' }} /> TV Shows</MenuItem>
                 </TextField>
                 <TextField
                    select label="Sort" value={searchSort} size="small" sx={{ minWidth: 110 }}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSearchSort(v);
                      // Re-run the search with the new ordering if there's a query
                      if (searchQuery.trim()) handleSearch(undefined, v);
                    }}
                 >
                    <MenuItem value={'size'}>Size ↓</MenuItem>
                    <MenuItem value={'seeds'}>Seeds ↓</MenuItem>
                 </TextField>
                 <Button variant="contained" onClick={handleSearch} disabled={searchLoading} startIcon={<SearchIcon />}>
                    Search
                 </Button>
               </Box>
                {searchError && <Alert severity="warning" sx={{ mb: 1 }}>{searchError}</Alert>}
            </Box>

            {/* Search Results Area */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              {searchLoading && (
                 <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <CircularProgress />
                 </Box>
              )}
              {!searchLoading && searchResults.length > 0 && (
                <List>
                   <AnimatePresence>
                    {searchResults.map((result, index) => {
                      // *** Check if the current result's key is in the selected set ***
                      const isSelected = selectedTorrents.has(result.key);
                      return (
                      <motion.div
                        key={result.key} // Use the generated unique key
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                         {/* *** Use ListItemButton for better click handling and styling *** */}
                         <ListItemButton
                            selected={isSelected}
                            onClick={() => handleSearchResultSelect(result)} // Toggle selection on click
                            sx={{
                                mb: 1, borderRadius: 2,
                                border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                                backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) },
                                pl: 1 // Add padding for checkbox
                             }}
                         >
                            {/* *** Add Checkbox for visual selection indicator *** */}
                            <ListItemIcon sx={{minWidth: 'auto', mr: 1.5}}>
                                <Checkbox
                                    edge="start"
                                    checked={isSelected}
                                    tabIndex={-1}
                                    disableRipple
                                    readOnly
                                    // Presentational only: the ListItemButton onClick owns the toggle.
                                    // (A direct onChange here would double-fire with the bubbling row click.)
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                   <Tooltip title={result.title} placement="top-start">
                                      <Typography noWrap> {result.title} </Typography>
                                   </Tooltip>
                                }
                                secondary={
                                    <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap" mt={0.5}>
                                       <Chip icon={<SizeIcon fontSize="small"/>} label={formatBytes(result.size)} size="small" variant="outlined" />
                                       <Chip icon={<SeedsIcon fontSize="small"/>} label={`${result.seeds} Seeds`} size="small" variant="outlined" color="success" />
                                       <Chip icon={<PeersIcon fontSize="small"/>} label={`${result.peers} Peers`} size="small" variant="outlined" color="info" />
                                       <Chip label={result.provider} size="small" />
                                       {!result.magnet && <Chip label="Magnet Error" size="small" color="error" />}
                                    </Box>
                                }
                            />
                         </ListItemButton>
                         {index < searchResults.length - 1 && <Divider component="li" />}
                      </motion.div>
                    )})}
                  </AnimatePresence>
                </List>
              )}
               {!searchLoading && searchResults.length === 0 && !searchError && (
                  <Typography color="text.secondary" textAlign="center" mt={4}>
                      Enter a search term and type to begin.
                  </Typography>
               )}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} color="inherit"> Cancel </Button>
        {tabValue === 0 && (
            <Button onClick={handleManualSubmit} variant="contained" disabled={manualLoading} startIcon={manualLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}>
            {manualLoading ? 'Adding...' : 'Add Manual Torrent(s)'}
            </Button>
        )}
         {tabValue === 1 && (
             // *** Update Search Add Button text and logic ***
            <Button
                onClick={handleAddSelectedTorrents} // Use the new handler
                variant="contained"
                disabled={selectedTorrents.size === 0 || searchLoading} // Disable if nothing selected or loading
                startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            >
             {searchLoading ? 'Adding...' : `Add ${selectedTorrents.size} Selected`}
            </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AddTorrentDialog;